import { Schema } from "effect";

/**
 * Base webhook response for successful processing
 * All webhook endpoints should return this schema
 */
export const WebhookSuccessResponse = Schema.Struct({
  status: Schema.Literal("ok"),
});
export type WebhookSuccessResponse = typeof WebhookSuccessResponse.Type;

/**
 * Google Pub/Sub push message format
 * Reference: https://cloud.google.com/pubsub/docs/push
 *
 * Note: Pub/Sub sends both camelCase and snake_case versions of fields
 * for compatibility. We only need to validate one version.
 */
export const GooglePubSubMessage = Schema.Struct({
  message: Schema.Struct({
    // Base64-encoded message data
    data: Schema.String,
    // Message ID assigned by Pub/Sub (camelCase and snake_case versions)
    messageId: Schema.String,
    // RFC3339 timestamp (camelCase and snake_case versions)
    publishTime: Schema.String,
  }),
  // Pub/Sub subscription name (format: projects/{project}/subscriptions/{subscription})
  subscription: Schema.String,
});
export type GooglePubSubMessage = typeof GooglePubSubMessage.Type;

/**
 * Gmail-specific webhook payload
 * The actual Gmail notification data is base64-encoded in message.data
 */
export const GmailWebhookPayload = GooglePubSubMessage;
export type GmailWebhookPayload = typeof GmailWebhookPayload.Type;

/**
 * Decoded Gmail webhook data (after base64 decoding message.data)
 * This matches the GmailWebhookData schema in gmail-inbound.ts
 *
 * Example decoded data: {"emailAddress":"info@misslola.com","historyId":212216066}
 */
export const GmailWebhookData = Schema.Struct({
  emailAddress: Schema.String,
  // Gmail sends historyId as a number, but we convert it to string for consistency
  historyId: Schema.transform(Schema.Number, Schema.String, {
    decode: String,
    encode: Number,
  }),
});
export type GmailWebhookData = typeof GmailWebhookData.Type;

/**
 * Schema that decodes base64-encoded message data and parses it as GmailWebhookData
 */
const Base64GmailWebhookData = Schema.compose(
  Schema.transform(Schema.String, Schema.String, {
    decode: (base64) => Buffer.from(base64, "base64").toString("utf-8"),
    encode: (str) => Buffer.from(str, "utf-8").toString("base64"),
  }),
  Schema.parseJson(GmailWebhookData),
);

/**
 * Gmail webhook payload with automatic base64 decoding
 * Use this to decode the full Pub/Sub message with automatic data parsing
 */
export const GmailWebhookPayloadWithDecodedData = Schema.Struct({
  message: Schema.Struct({
    data: Base64GmailWebhookData,
    messageId: Schema.String,
    publishTime: Schema.String,
  }),
  subscription: Schema.String,
});
export type GmailWebhookPayloadWithDecodedData =
  typeof GmailWebhookPayloadWithDecodedData.Type;

/**
 * Microsoft Graph change notification resource data
 * Contains the ID of the changed resource
 */
export const GraphChangeNotificationResourceData = Schema.Struct({
  // OData type (e.g., "#Microsoft.Graph.Message")
  "@odata.type": Schema.String,
  // OData ID (e.g., "Users/{user-id}/Messages/{message-id}")
  "@odata.id": Schema.String,
  // OData etag
  "@odata.etag": Schema.String,
  // The ID of the changed resource
  id: Schema.String,
});
export type GraphChangeNotificationResourceData =
  typeof GraphChangeNotificationResourceData.Type;

/**
 * Single Microsoft Graph change notification
 */
export const GraphChangeNotification = Schema.Struct({
  // Type of change: "created", "updated", "deleted"
  changeType: Schema.String,
  // Client state string that was set during subscription creation (for validation)
  clientState: Schema.optional(Schema.String),
  // The resource that changed (e.g., "Users/{user-id}/Messages/{message-id}")
  resource: Schema.String,
  // Resource data with the changed resource ID
  resourceData: GraphChangeNotificationResourceData,
  // Subscription ID
  subscriptionId: Schema.String,
  // Expiration time of the subscription
  subscriptionExpirationDateTime: Schema.String,
  // Tenant ID
  tenantId: Schema.String,
});
export type GraphChangeNotification = typeof GraphChangeNotification.Type;

/**
 * Microsoft Graph webhook payload containing one or more change notifications
 * Reference: https://learn.microsoft.com/en-us/graph/webhooks
 */
export const OutlookWebhookPayload = Schema.Struct({
  value: Schema.Array(GraphChangeNotification),
});
export type OutlookWebhookPayload = typeof OutlookWebhookPayload.Type;

/**
 * Generic webhook validation token response
 * Used for webhook endpoint verification (e.g., Microsoft Graph validation)
 */
export const WebhookValidationResponse = Schema.Struct({
  validationToken: Schema.String,
});
export type WebhookValidationResponse = typeof WebhookValidationResponse.Type;
